import React, { useEffect, useRef, useState } from "react";
import { IoClose } from "react-icons/io5";

const ModalComponent = ({ isOpen, onClose, children, contentClassName = "" }) => {
    const contentItemRef = useRef(null);
    const [scrollIndicator, setScrollIndicator] = useState({
        isVisible: false,
        trackHeight: 0,
        thumbHeight: 0,
        thumbTop: 0,
    });
    const [contentWidth, setContentWidth] = useState(393);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [isOpen]);

    useEffect(() => {
        const contentItem = contentItemRef.current;
        if (!isOpen || !contentItem || !contentClassName.includes("course-info-scroll")) {
            setContentWidth(393);
            return;
        }

        const fitPopupToLongestWord = () => {
            const infoBlocks = contentItem.querySelectorAll(".course-info-modal");
            const words = Array.from(infoBlocks)
                .flatMap(block => (block.innerText || "").trim().split(/\s+/))
                .filter(Boolean);

            if (!words.length) {
                setContentWidth(393);
                return;
            }

            const sampleBlock = infoBlocks[0];
            const computed = window.getComputedStyle(sampleBlock);
            const measureElement = document.createElement("span");
            Object.assign(measureElement.style, {
                position: "absolute",
                visibility: "hidden",
                whiteSpace: "nowrap",
                fontFamily: computed.fontFamily,
                fontSize: computed.fontSize,
                fontStyle: computed.fontStyle,
                fontWeight: computed.fontWeight,
                letterSpacing: computed.letterSpacing,
            });
            document.body.appendChild(measureElement);

            const longestWordWidth = Math.max(...words.map(word => {
                measureElement.textContent = word;
                return measureElement.getBoundingClientRect().width;
            }));
            measureElement.remove();

            const horizontalPadding = parseFloat(computed.paddingLeft) + parseFloat(computed.paddingRight);
            const requiredWidth = Math.ceil(longestWordWidth + horizontalPadding + 18);
            const availableScreenWidth = Math.max(window.innerWidth - 32, 0);

            setContentWidth(Math.min(Math.max(393, requiredWidth), availableScreenWidth));
        };

        const animationFrame = requestAnimationFrame(fitPopupToLongestWord);
        document.fonts?.ready.then(fitPopupToLongestWord);
        window.addEventListener("resize", fitPopupToLongestWord);

        return () => {
            cancelAnimationFrame(animationFrame);
            window.removeEventListener("resize", fitPopupToLongestWord);
        };
    }, [isOpen, children, contentClassName]);

    useEffect(() => {
        const contentItem = contentItemRef.current;
        if (!isOpen || !contentItem || !contentClassName.includes("course-info-scroll")) return;

        const updateScrollIndicator = () => {
            const { clientHeight, scrollHeight, scrollTop } = contentItem;
            const isVisible = scrollHeight > clientHeight;
            const trackHeight = Math.max(clientHeight - 24, 0);
            const thumbHeight = isVisible
                ? Math.max(trackHeight * clientHeight / scrollHeight, 48)
                : 0;
            const maxThumbTop = Math.max(trackHeight - thumbHeight, 0);
            const maxScrollTop = Math.max(scrollHeight - clientHeight, 1);

            setScrollIndicator({
                isVisible,
                trackHeight,
                thumbHeight,
                thumbTop: maxThumbTop * scrollTop / maxScrollTop,
            });
        };

        const animationFrame = requestAnimationFrame(updateScrollIndicator);
        contentItem.addEventListener("scroll", updateScrollIndicator);
        window.addEventListener("resize", updateScrollIndicator);

        return () => {
            cancelAnimationFrame(animationFrame);
            contentItem.removeEventListener("scroll", updateScrollIndicator);
            window.removeEventListener("resize", updateScrollIndicator);
        };
    }, [isOpen, children, contentClassName, contentWidth]);

    if (!isOpen) return null;

    return (
        <>
            <button type="button" className="course-modal-close text-white modal-close-icon d-none d-lg-block" aria-label="Закрыть" onClick={onClose}><IoClose size={40} aria-hidden="true" /></button>
            <div className={`custom-modal-layer ${isOpen ? 'show' : ''}`} onClick={onClose}></div>
            <div className={`custom-modal ${isOpen ? 'show' : ''} ${contentClassName.includes("course-info-scroll") ? 'course-description-modal' : ''}`}>
                <button type="button" className="course-modal-close modal-close-icon-mobile d-block d-lg-none" aria-label="Закрыть" onClick={onClose}><IoClose size={30} aria-hidden="true" /></button>
                <div className="content radius-20" style={{ width: `${contentWidth}px` }}>
                    <div ref={contentItemRef} className={`content-item ${contentClassName}`}>
                        {children}
                    </div>
                    {scrollIndicator.isVisible ?
                        <div className="course-info-scrollbar" style={{ height: `${scrollIndicator.trackHeight}px` }} aria-hidden="true">
                            <div
                                className="course-info-scrollbar-thumb"
                                style={{
                                    height: `${scrollIndicator.thumbHeight}px`,
                                    transform: `translateY(${scrollIndicator.thumbTop}px)`,
                                }}
                            />
                        </div>
                        : null}
                </div>
            </div>
        </>
    );
};

export default ModalComponent;
